import { useState, useEffect, useCallback } from 'react';
import { AppShell } from '@/components/layout/AppShell';
import { Card, Button, Badge, DataTable, Column } from '@/components/ui';
import {
  Settings,
  RefreshCw,
  Loader2,
  Cpu,
  Fingerprint,
  Activity,
  AlertTriangle,
  Globe,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { useDateFormat } from '@/hooks/useDateFormat';
import { provisioningService } from '@/services/provisioningService';
import { cn } from '@/lib/utils';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '@/config/firebase';

interface VendorConfig {
  id: string;
  vendorId: string;
  vendorName: string;
  baseUrl: string;
  isActive: boolean;
  metadata: Record<string, any>;
  lastHealthCheck?: any;
  lastHealthStatus?: string;
}

interface ProvisioningJob {
  id: string;
  operation: string;
  eid: string;
  iccid?: string;
  status: string;
  currentStage?: string;
  error?: string;
  createdAt: any;
  completedAt?: any;
}

interface ModemWithEid {
  deviceId: string;
  deviceName: string;
  modemId: string;
  imei: string;
  model?: string;
  eid?: string;
  locationName?: string;
}

const statusBadgeVariant = (status: string): 'default' | 'secondary' | 'destructive' | 'outline' => {
  switch (status) {
    case 'completed': return 'default';
    case 'failed': case 'timeout': return 'destructive';
    default: return 'secondary';
  }
};

export default function ESimProvisioning() {
  const { activeOrganizationId, systemRole } = useAuth();
  const { success: toastSuccess, error: toastError } = useToast();
  const fmt = useDateFormat();
  const orgId = activeOrganizationId;
  const isSuperAdmin = systemRole === 'super_admin';

  const [vendorConfig, setVendorConfig] = useState<VendorConfig | null>(null);
  const [jobs, setJobs] = useState<ProvisioningJob[]>([]);
  const [modems, setModems] = useState<ModemWithEid[]>([]);
  const [loading, setLoading] = useState(true);
  const [healthChecking, setHealthChecking] = useState(false);
  const [discoveringEid, setDiscoveringEid] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'devices' | 'jobs' | 'config'>('devices');

  const loadModems = async () => {
    try {
      const snapshot = await getDocs(collection(db, 'edge_devices'));
      const modemList: ModemWithEid[] = [];
      for (const doc of snapshot.docs) {
        const data = doc.data();
        const deviceModems = data.modems || [];
        for (const modem of deviceModems) {
          if (modem.imei) {
            modemList.push({
              deviceId: doc.id,
              deviceName: data.name || doc.id,
              modemId: modem.id || modem.modemId || modem.imei,
              imei: modem.imei,
              model: modem.model || modem.modemModelName,
              eid: modem.simInfo?.eid,
              locationName: data.locationName || data.locationId,
            });
          }
        }
      }
      setModems(modemList);
    } catch (err) {
      console.error('Failed to load modems:', err);
    }
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Load modems from Firestore (doesn't need orgId)
      await loadModems();

      // Only load vendor config and jobs if we have an org
      if (orgId) {
        const [config, jobsData] = await Promise.all([
          provisioningService.getVendorConfig(orgId).catch(() => null),
          provisioningService.getJobs(orgId).catch(() => []),
        ]);
        setVendorConfig(config);
        setJobs(jobsData);
      }
    } catch (err: any) {
      console.error('Failed to load provisioning data:', err);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleHealthCheck = async () => {
    if (!vendorConfig) return;
    setHealthChecking(true);
    try {
      const result = await provisioningService.healthCheck(vendorConfig.id);
      if (result.healthy) {
        toastSuccess(result.message, 'Vendor Connected');
      } else {
        toastError(result.message, 'Connection Failed');
      }
      await loadData();
    } catch (err: any) {
      toastError(err.message, 'Health Check Failed');
    } finally {
      setHealthChecking(false);
    }
  };

  const handleDiscoverEid = async (modem: ModemWithEid) => {
    setDiscoveringEid(modem.imei);
    try {
      const result = await provisioningService.discoverEid(modem.deviceId, modem.imei);
      if (result.success && result.eid) {
        toastSuccess(`EID: ${result.eid}`, 'EID Discovered');
        await loadModems();
      } else {
        toastError(result.message, 'EID Not Found');
      }
    } catch (err: any) {
      toastError(err.message, 'Discovery Failed');
    } finally {
      setDiscoveringEid(null);
    }
  };

  const modemColumns: Column<ModemWithEid>[] = [
    {
      key: 'device',
      header: 'Device',
      render: (_v, row) => (
        <div>
          <div className="font-medium text-foreground">{row.deviceName}</div>
          <div className="text-xs text-muted-foreground">{row.locationName || 'No location'}</div>
        </div>
      ),
    },
    {
      key: 'modem',
      header: 'Modem',
      render: (_v, row) => (
        <div>
          <div className="text-foreground">{row.model || 'Unknown'}</div>
          <div className="text-xs text-muted-foreground font-mono">IMEI: {row.imei}</div>
        </div>
      ),
    },
    {
      key: 'eid',
      header: 'EID (eUICC)',
      render: (_v, row) => (
        <div className="flex items-center gap-2">
          {row.eid ? (
            <div>
              <Badge variant="default" className="text-xs">Discovered</Badge>
              <div className="text-xs font-mono text-muted-foreground mt-1 break-all">
                {row.eid}
              </div>
            </div>
          ) : (
            <Badge variant="secondary">Not discovered</Badge>
          )}
        </div>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (_v, row) => (
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleDiscoverEid(row)}
          disabled={discoveringEid === row.imei}
        >
          {discoveringEid === row.imei ? (
            <Loader2 className="h-3 w-3 animate-spin mr-1" />
          ) : (
            <Fingerprint className="h-3 w-3 mr-1" />
          )}
          {row.eid ? 'Re-discover' : 'Discover EID'}
        </Button>
      ),
    },
  ];

  const jobColumns: Column<ProvisioningJob>[] = [
    {
      key: 'status',
      header: 'Status',
      render: (_v, row) => (
        <Badge variant={statusBadgeVariant(row.status)}>{row.status}</Badge>
      ),
    },
    { key: 'operation', header: 'Operation' },
    {
      key: 'eid',
      header: 'EID',
      render: (_v, row) => (
        <span className="font-mono text-xs">{row.eid?.substring(0, 16)}...</span>
      ),
    },
    {
      key: 'iccid',
      header: 'ICCID',
      render: (_v, row) => (
        <span className="font-mono text-xs">{row.iccid || '\u2014'}</span>
      ),
    },
    {
      key: 'stage',
      header: 'Stage',
      render: (_v, row) => (
        <span className="text-sm text-muted-foreground">{row.currentStage || row.error || '\u2014'}</span>
      ),
    },
    {
      key: 'created',
      header: 'Created',
      render: (_v, row) => {
        const ts = row.createdAt;
        const date = ts?.toDate ? ts.toDate() : ts?._seconds ? new Date(ts._seconds * 1000) : new Date(ts);
        return <span className="text-xs text-muted-foreground">{fmt.formatDateTime(date)}</span>;
      },
    },
  ];

  if (loading) {
    return (
      <AppShell>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">SGP.32 Provisioning</h1>
            <p className="text-muted-foreground">
              Manage eSIM profile provisioning via vendor platforms
            </p>
          </div>
          <Button variant="outline" onClick={loadData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>

        <div className="flex gap-1 border-b border-border">
          {(['devices', 'jobs', 'config'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={cn(
                'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
                activeTab === tab
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              )}
            >
              {tab === 'devices' && 'Devices & EIDs'}
              {tab === 'jobs' && `Jobs (${jobs.length})`}
              {tab === 'config' && 'Vendor Config'}
            </button>
          ))}
        </div>

        {activeTab === 'devices' && (
          <Card className="p-0">
            <div className="p-4 border-b border-border">
              <h3 className="font-semibold text-foreground flex items-center gap-2">
                <Cpu className="h-4 w-4" />
                Modems & eUICC EIDs
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                Discover EIDs from modem hardware. The EID is the permanent identifier that links a modem to the SGP.32 vendor platform.
              </p>
            </div>
            {modems.length > 0 ? (
              <DataTable
                data={modems}
                columns={modemColumns}
                keyExtractor={(row) => `${row.deviceId}-${row.imei}`}
              />
            ) : (
              <div className="p-8 text-center text-muted-foreground">
                No modems found. Edge devices must be registered first.
              </div>
            )}
          </Card>
        )}

        {activeTab === 'jobs' && (
          <Card className="p-0">
            <div className="p-4 border-b border-border">
              <h3 className="font-semibold text-foreground flex items-center gap-2">
                <Activity className="h-4 w-4" />
                Provisioning Jobs
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                History of all SGP.32 provisioning operations (import, push, enable, disable).
              </p>
            </div>
            {jobs.length > 0 ? (
              <DataTable
                data={jobs}
                columns={jobColumns}
                keyExtractor={(row) => row.id}
              />
            ) : (
              <div className="p-8 text-center text-muted-foreground">
                No provisioning jobs yet. Jobs are created when flows execute with eSIM profiles.
              </div>
            )}
          </Card>
        )}

        {activeTab === 'config' && (
          <Card className="p-6">
            <h3 className="font-semibold text-foreground flex items-center gap-2 mb-4">
              <Settings className="h-4 w-4" />
              Vendor Configuration
            </h3>

            {vendorConfig ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-muted-foreground">Vendor</label>
                    <p className="text-foreground font-medium">{vendorConfig.vendorName}</p>
                  </div>
                  <div>
                    <label className="text-sm text-muted-foreground">Status</label>
                    <div className="flex items-center gap-2">
                      {vendorConfig.lastHealthStatus === 'healthy' ? (
                        <Badge variant="default">Healthy</Badge>
                      ) : vendorConfig.lastHealthStatus === 'unhealthy' ? (
                        <Badge variant="destructive">Unhealthy</Badge>
                      ) : (
                        <Badge variant="secondary">Not checked</Badge>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm text-muted-foreground">Base URL</label>
                    <p className="text-foreground font-mono text-sm">{vendorConfig.baseUrl}</p>
                  </div>
                  <div>
                    <label className="text-sm text-muted-foreground">Customer ID</label>
                    <p className="text-foreground">{vendorConfig.metadata?.customerId || '\u2014'}</p>
                  </div>
                </div>

                <div className="flex gap-2 pt-2">
                  <Button
                    variant="outline"
                    onClick={handleHealthCheck}
                    disabled={healthChecking}
                  >
                    {healthChecking ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Activity className="h-4 w-4 mr-2" />
                    )}
                    Test Connection
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <AlertTriangle className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                <p className="text-muted-foreground">
                  No SGP.32 vendor configured for this organization.
                </p>
                {isSuperAdmin && (
                  <p className="text-sm text-muted-foreground mt-1">
                    Use the API to create a vendor config: POST /api/provisioning/config
                  </p>
                )}
              </div>
            )}
          </Card>
        )}
      </div>
    </AppShell>
  );
}
